<script setup>
  import {computed, inject, ref, watch} from "vue";

  import ComponentPrivacyGuard from "@components/Alerts/PrivacyGuard.vue";

  defineOptions({
    name: "ComponentTextarea",
  });

  const props = defineProps({
    name: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    modelValue: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "",
    },
    maxlength: {
      type: [Number, String],
      default: null,
    },
    validation: {
      type: String,
      default: null,
    },
    privacyGuard: {
      type: [String, null],
      default: undefined, // undefined, when attribute is not set at all
    },
    helperText: {
      type: String,
      default: null,
    },
    rows: {
      type: [Number, String],
      default: 5,
    },
    background: {
      type: String,
      default: "bg-white",
    },
    encrypted: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits([
    "keyup",
    "keydown",
    "blur",
    "input",
    "changed",
    "change",
    "update:modelValue",
    "decrypted",
  ]);

  const privacy = inject("$privacy");

  const refTextarea = ref(null);

  const fieldId = ref(crypto.randomUUID());
  const currentValue = ref(null);
  const internalValue = ref(props.modelValue);

  const isInvalid = computed(() => props.validation);

  const currentLength = computed(() => {
    if (!currentValue.value) {
      return 0;
    }
    return currentValue.value.length;
  });

  watch(
    () => props.modelValue,
    (newValue) => {
      internalValue.value = newValue;
    },
  );

  watch(
    internalValue,
    (newValue) => {
      if (props.encrypted) {
        privacy.decryptValue(internalValue.value).then((plaintext) => {
          currentValue.value = plaintext;
          emit("decrypted", plaintext);
        });
      } else {
        currentValue.value = newValue;
      }
    },
    {immediate: true},
  );

  const handleInput = () => {
    if (props.encrypted && currentValue.value.trim() !== "") {
      // encrypt non-empty values
      privacy.encryptValue(currentValue.value).then((hash) => {
        internalValue.value = hash;

        emit("update:modelValue", internalValue.value);
        emit("input", internalValue.value);
        emit("change", internalValue.value);
      });
    } else {
      // set empty values to null
      internalValue.value = currentValue.value || null;

      emit("update:modelValue", internalValue.value);
      emit("input", internalValue.value);
      emit("change", internalValue.value);
    }
  };

  const focus = () => {
    refTextarea.value.focus();
  };

  defineExpose({
    focus,
  });
</script>

<template>
  <div class="w-full" :data-test="getTestId('textarea-container')">
    <label
      :class="{'text-mcred': isInvalid || privacyGuard, 'text-gray-500': !isInvalid & !large, 'text-sm': !large}"
      :for="fieldId"
      :data-test="getTestId('textarea-label')"
    >
      {{ label }}
    </label>

    <textarea
      :id="fieldId"
      ref="refTextarea"
      v-model="currentValue"
      class="resize-y overflow-y-auto"
      :class="background"
      :rows="rows"
      autocomplete="off"
      :name="name"
      :placeholder="placeholder"
      :disabled="disabled"
      :maxlength="maxlength"
      :data-test="getTestId('textarea')"
      @input="handleInput"
      @keyup="$emit('keyup', $event)"
      @keydown="$emit('keydown', $event)"
      @blur="$emit('blur', $event)"
    />

    <input v-model="internalValue" type="hidden" :data-test="getTestId('textarea-internal-value')" />

    <div class="mt-1 flex cursor-default justify-between space-x-2">
      <div class="text-xs">
        <div
          v-if="typeof privacyGuard === 'undefined' || isInvalid"
          class="text-mcred"
          :data-test="getTestId('textarea-error-message')"
        >
          {{ validation }}
        </div>
        <div v-if="typeof privacyGuard !== 'undefined'" :data-test="getTestId('textarea-privacy-guard')">
          <slot v-if="$slots['privacy-guard']" name="privacy-guard" />
          <component-privacy-guard v-else :validation="privacyGuard" />
        </div>

        <div
          v-if="($slots['helper-text'] || helperText) && !isInvalid"
          class="text-gray-500"
          :data-test="getTestId('textarea-helper-text')"
        >
          <slot name="helper-text" />
          {{ helperText }}
        </div>
      </div>

      <div
        v-if="maxlength && !disabled"
        class="text-right text-xs text-gray-500"
        :class="{'text-mcred': currentLength >= maxlength}"
        :data-test="getTestId('textarea-max-length')"
      >
        {{ currentLength }}/{{ maxlength }}
      </div>
    </div>
  </div>
</template>
